/**
 * Forgot Password Page
 * Business owner password reset — sends Twilio Verify SMS to the phone on file
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Input, Card, toast } from '../components/ui';
import apiClient from '../lib/api';

export const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { phone, userType: 'BUSINESS' });
      toast.success('Check your phone for a verification code');
      navigate(`/reset-password?phone=${encodeURIComponent(phone)}`);
    } catch {
      // The backend deliberately doesn't reveal whether the account exists,
      // so forward the user to the next step regardless.
      toast.success('Check your phone for a verification code');
      navigate(`/reset-password?phone=${encodeURIComponent(phone)}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-orange-400 mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-h1 text-black font-bold">Buffables</h1>
          <p className="text-base text-black-lighter mt-2">Business Dashboard</p>
        </div>

        <Card>
          <div className="p-8">
            <h2 className="text-h3 text-black font-semibold mb-2">Reset your password</h2>
            <p className="text-base text-black-lighter mb-6">
              Enter the phone number on your business account. We'll text you a verification code.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <Input
                type="tel"
                id="phone"
                label="Phone number"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                isRequired
                autoComplete="tel"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Send verification code
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-base text-orange-400 hover:text-orange-300 font-semibold transition-colors"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
